import { ConditionGroup, NextPageParams, PaginationMetadata, Report, ReportWithCohorts, ViewSort } from "@/schema"

import { FilterReportDefinition } from "@/components/reports/filter-report-definition"

import { ApiContext, apiContextFetch } from "../base"
import { appendConditionGroupParams, appendViewSortParams } from "../condition-group-params"

export interface ReportsResponse {
  reports: Report[]
  pagination: PaginationMetadata
}

interface FetchReportsOptions {
  conditionGroup?: ConditionGroup
  searchTerm?: string
  viewSort?: ViewSort
  nextPageParams?: NextPageParams
  includeCompany?: boolean
}

interface RunReportResponse {
  chat_thread_id: string
}

export function fetchReports(context: ApiContext, options: FetchReportsOptions = {}) {
  const { conditionGroup, searchTerm, viewSort, nextPageParams, includeCompany = false } = options
  const params = new URLSearchParams()

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (conditionGroup) {
    appendConditionGroupParams(params, conditionGroup)
  }

  if (includeCompany) params.append("include_company", "true")

  appendViewSortParams(params, viewSort)

  const query = params.toString()
  return apiContextFetch<ReportsResponse>(context, query ? `reports?${query}` : `reports`)
}

export function fetchReport(context: ApiContext, id: string) {
  return apiContextFetch<ReportWithCohorts>(context, `reports/${id}`)
}

export function createReport(
  context: ApiContext,
  params: { name?: string; source_cohort_id?: string; query?: string } | FilterReportDefinition
) {
  return apiContextFetch<Report>(context, `reports`, {
    method: "POST",
    body: JSON.stringify({ report: params }),
  })
}

export function updateReport(context: ApiContext, id: string, params: { name: string } | FilterReportDefinition) {
  return apiContextFetch<ReportWithCohorts>(context, `reports/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ report: params }),
  })
}

export function deleteReport(context: ApiContext, id: string) {
  return apiContextFetch<void>(context, `reports/${id}`, {
    method: "DELETE",
  })
}

export function runReport(context: ApiContext, id: string, additionalInstructions?: string) {
  return apiContextFetch<RunReportResponse>(context, `reports/${id}/run`, {
    method: "POST",
    body: additionalInstructions ? JSON.stringify({ additional_instructions: additionalInstructions }) : undefined,
  })
}
